import type { KioskAccountSessionClaim } from '@kangaroo/shared';

export const KIOSK_BROWSER_LEASE_STORAGE_KEY = 'paddiq:kiosk-browser-lease-v1';
export const KIOSK_ACCOUNT_TERMINATION_STORAGE_KEY = 'paddiq:kiosk-account-termination-v1';

export const KIOSK_CLIENT_TERMINATION_REASONS = [
  'USER_DONE',
  'INACTIVITY',
  'TOKEN_EXPIRED',
  'EXPLICIT_CLEAR',
] as const;

export type KioskClientTerminationReason = (typeof KIOSK_CLIENT_TERMINATION_REASONS)[number];

export type KioskBrowserLease = {
  stationId: string;
  id: string;
  generation: number;
};

export type KioskAccountTerminationLock = {
  version: 1;
  stationId: string;
  session: KioskAccountSessionClaim;
  reason: KioskClientTerminationReason;
  createdAt: number;
  corrupt?: true;
};

export type KioskAccountTerminationReceipt = {
  accountSessionId: string;
  accountSessionGeneration: number;
  terminatedAt: string;
  terminationReason: string;
  status: 'TERMINATED' | 'ALREADY_TERMINATED';
};

let inMemoryTerminationLock: KioskAccountTerminationLock | null = null;
let authenticatedRequestCanceler: () => void = () => undefined;

export function registerAuthenticatedRequestCanceler(canceler: () => void) {
  authenticatedRequestCanceler = canceler;
}

export function cancelRegisteredAuthenticatedRequests() {
  authenticatedRequestCanceler();
}

export function isKioskAccountSessionClaim(value: unknown): value is KioskAccountSessionClaim {
  if (!value || typeof value !== 'object') return false;
  const claim = value as Partial<KioskAccountSessionClaim>;
  return (
    claim.version === 1 &&
    typeof claim.stationId === 'string' &&
    claim.stationId.length > 0 &&
    typeof claim.provisioningGeneration === 'number' &&
    Number.isInteger(claim.provisioningGeneration) &&
    claim.provisioningGeneration >= 0 &&
    typeof claim.associationId === 'string' &&
    claim.associationId.length > 0 &&
    typeof claim.associationGeneration === 'number' &&
    Number.isInteger(claim.associationGeneration) &&
    claim.associationGeneration >= 0 &&
    typeof claim.accountSessionId === 'string' &&
    claim.accountSessionId.length > 0 &&
    typeof claim.accountSessionGeneration === 'number' &&
    Number.isInteger(claim.accountSessionGeneration) &&
    claim.accountSessionGeneration >= 0 &&
    typeof claim.browserLeaseId === 'string' &&
    claim.browserLeaseId.length > 0 &&
    typeof claim.browserLeaseGeneration === 'number' &&
    Number.isInteger(claim.browserLeaseGeneration) &&
    claim.browserLeaseGeneration >= 0
  );
}

function parseBrowserLease(raw: string | null): KioskBrowserLease | null {
  if (!raw) return null;
  try {
    const lease = JSON.parse(raw) as Partial<KioskBrowserLease>;
    return typeof lease.stationId === 'string' &&
      typeof lease.id === 'string' &&
      lease.id.length > 0 &&
      typeof lease.generation === 'number' &&
      Number.isInteger(lease.generation) &&
      lease.generation >= 0
      ? { stationId: lease.stationId, id: lease.id, generation: lease.generation }
      : null;
  } catch {
    return null;
  }
}

export function nextKioskBrowserLease(
  storage: Pick<Storage, 'getItem' | 'setItem'> | null,
  stationId: string,
  createId = () => crypto.randomUUID(),
): KioskBrowserLease {
  let previous: KioskBrowserLease | null = null;
  try {
    previous = parseBrowserLease(storage?.getItem(KIOSK_BROWSER_LEASE_STORAGE_KEY) ?? null);
  } catch {
    // A fresh in-memory lease still fails closed if durable storage is unavailable.
  }
  const lease = {
    stationId,
    id: previous?.stationId === stationId ? previous.id : createId(),
    generation: previous?.stationId === stationId ? previous.generation + 1 : 1,
  };
  try {
    storage?.setItem(KIOSK_BROWSER_LEASE_STORAGE_KEY, JSON.stringify(lease));
  } catch {
    // The server tuple remains exact for this attempt even without persistence.
  }
  return lease;
}

export function createKioskAccountTerminationLock(
  session: KioskAccountSessionClaim,
  reason: KioskClientTerminationReason,
  createdAt = Date.now(),
): KioskAccountTerminationLock {
  return { version: 1, stationId: session.stationId, session, reason, createdAt };
}

function parseTerminationLock(raw: string | null): KioskAccountTerminationLock | null {
  if (!raw) return null;
  try {
    const lock = JSON.parse(raw) as Partial<KioskAccountTerminationLock>;
    return lock.version === 1 &&
      typeof lock.stationId === 'string' &&
      isKioskAccountSessionClaim(lock.session) &&
      lock.session.stationId === lock.stationId &&
      KIOSK_CLIENT_TERMINATION_REASONS.includes(lock.reason as KioskClientTerminationReason) &&
      typeof lock.createdAt === 'number' &&
      Number.isFinite(lock.createdAt)
      ? (lock as KioskAccountTerminationLock)
      : null;
  } catch {
    return null;
  }
}

function corruptTerminationLock(raw: string): KioskAccountTerminationLock {
  let stationId = '__unrecoverable_kiosk_account_session__';
  try {
    const candidate = JSON.parse(raw) as { stationId?: unknown };
    if (typeof candidate.stationId === 'string' && candidate.stationId.length > 0) {
      stationId = candidate.stationId;
    }
  } catch {
    // The opaque durable value is intentionally retained for fail-closed recovery.
  }
  return {
    version: 1,
    stationId,
    session: {
      version: 1,
      stationId,
      provisioningGeneration: 0,
      associationId: '__unrecoverable__',
      associationGeneration: 0,
      accountSessionId: '__unrecoverable__',
      accountSessionGeneration: 0,
      browserLeaseId: '__unrecoverable__',
      browserLeaseGeneration: 0,
    },
    reason: 'EXPLICIT_CLEAR',
    createdAt: Date.now(),
    corrupt: true,
  };
}

export function isCorruptKioskAccountTerminationLock(lock: KioskAccountTerminationLock) {
  return lock.corrupt === true;
}

export function persistKioskAccountTerminationLock(
  storage: Pick<Storage, 'setItem'> | null,
  lock: KioskAccountTerminationLock,
) {
  inMemoryTerminationLock = lock;
  try {
    storage?.setItem(KIOSK_ACCOUNT_TERMINATION_STORAGE_KEY, JSON.stringify(lock));
  } catch {
    // The module-level lock preserves concealment and blocks this running kiosk.
  }
}

export function restoreKioskAccountTerminationLock(
  storage: Pick<Storage, 'getItem' | 'removeItem'> | null,
): KioskAccountTerminationLock | null {
  if (inMemoryTerminationLock) return inMemoryTerminationLock;
  try {
    const raw = storage?.getItem(KIOSK_ACCOUNT_TERMINATION_STORAGE_KEY) ?? null;
    const lock = parseTerminationLock(raw);
    inMemoryTerminationLock = raw && !lock ? corruptTerminationLock(raw) : lock;
    return inMemoryTerminationLock;
  } catch {
    return null;
  }
}

export function receiptMatchesKioskTerminationLock(
  receipt: KioskAccountTerminationReceipt,
  lock: KioskAccountTerminationLock,
) {
  if (isCorruptKioskAccountTerminationLock(lock)) return false;
  return (
    receipt.accountSessionId === lock.session.accountSessionId &&
    receipt.accountSessionGeneration === lock.session.accountSessionGeneration &&
    (receipt.status === 'TERMINATED' || receipt.status === 'ALREADY_TERMINATED')
  );
}

export function removeKioskAccountTerminationLock(
  storage: Pick<Storage, 'getItem' | 'removeItem'> | null,
  expected: KioskAccountTerminationLock,
) {
  const matches = (lock: KioskAccountTerminationLock | null) =>
    lock?.session.accountSessionId === expected.session.accountSessionId &&
    lock.session.accountSessionGeneration === expected.session.accountSessionGeneration;
  if (matches(inMemoryTerminationLock)) inMemoryTerminationLock = null;
  try {
    const stored = parseTerminationLock(
      storage?.getItem(KIOSK_ACCOUNT_TERMINATION_STORAGE_KEY) ?? null,
    );
    if (matches(stored)) storage?.removeItem(KIOSK_ACCOUNT_TERMINATION_STORAGE_KEY);
  } catch {
    // A receipt is authoritative even if durable lock removal must be retried later.
  }
}

type TimerHandle = ReturnType<typeof setTimeout>;

export function kioskAccountTerminationRetryDelay(attempt: number) {
  return Math.min(30_000, 1_000 * 2 ** Math.max(0, attempt));
}

export function startKioskAccountTerminationRetry(input: {
  request: (signal: AbortSignal) => Promise<KioskAccountTerminationReceipt>;
  onRetry: (attempt: number) => void;
  onConfirmed: (receipt: KioskAccountTerminationReceipt) => void;
  onExhausted: () => void;
  maxAttempts?: number;
  requestTimeoutMs?: number;
  setTimer?: (callback: () => void, delayMs: number) => TimerHandle;
  clearTimer?: (handle: TimerHandle | undefined) => void;
}) {
  const setTimer = input.setTimer ?? ((callback, delayMs) => setTimeout(callback, delayMs));
  const clearTimer = input.clearTimer ?? ((handle) => clearTimeout(handle));
  const maxAttempts = input.maxAttempts ?? 5;
  const requestTimeoutMs = input.requestTimeoutMs ?? 8_000;
  let cancelled = false;
  let retryTimer: TimerHandle | undefined;
  let timeoutTimer: TimerHandle | undefined;
  let activeController: AbortController | null = null;

  const run = async (attempt: number) => {
    const controller = new AbortController();
    activeController = controller;
    timeoutTimer = setTimer(() => controller.abort(), requestTimeoutMs);
    try {
      const receipt = await input.request(controller.signal);
      if (!cancelled) input.onConfirmed(receipt);
    } catch {
      if (cancelled) return;
      const nextAttempt = attempt + 1;
      input.onRetry(nextAttempt);
      if (nextAttempt >= maxAttempts) {
        input.onExhausted();
        return;
      }
      retryTimer = setTimer(
        () => void run(nextAttempt),
        kioskAccountTerminationRetryDelay(attempt),
      );
    } finally {
      clearTimer(timeoutTimer);
      timeoutTimer = undefined;
      if (activeController === controller) activeController = null;
    }
  };

  // Deferring the first attempt lets React's development-only effect replay cancel the
  // provisional run before it reaches the network. The durable lock remains the authority.
  retryTimer = setTimer(() => void run(0), 0);
  return () => {
    cancelled = true;
    activeController?.abort();
    clearTimer(timeoutTimer);
    clearTimer(retryTimer);
  };
}
